import { SupportedChainId, contractBook } from "@eulerxyz/core-sdk"
import {
  Address,
  Client,
  Hex,
  encodeFunctionData,
  publicActions,
  zeroAddress,
} from "viem"
import { EVCCall } from "../evc"
import { cachedQuery } from "./cache"

type PythAdapter = {
  name: "PythOracle"
  feedId: Hex
  whitelisted: boolean
}

type OracleAdapter =
  | {
      name: string
      whitelisted: boolean
    }
  | PythAdapter

interface PythPriceResponse {
  binary: {
    data: string
  }
}

/**
 * @notice Fetch the Oracle adapters for a given chain ID
 * @param chainId The chain ID
 * @returns The Oracle adapters for the given chain ID
 */
/**
 * @notice Fetch the Oracle adapters for a given chain ID
 * @param chainId The chain ID
 * @returns The Oracle adapters for the given chain ID
 */
async function fetchOracleAdapters(
  chainId: SupportedChainId,
): Promise<string[]> {
  const response = await fetch(
    `https://oracle-checks-data.euler.finance/${chainId}/adapters/all.json`,
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch oracles for chain ${chainId}`)
  }

  const data: OracleAdapter[] = await response.json()
  return data
    .filter(isPythAdapter)
    .map(({ feedId }) => feedId.replace("0x", ""))
}
/**
 * @notice Fetch the Pyth prices for a given block number
 * @param pythAdapters The Pyth adapters
 * @returns The Pyth prices for the given block number
 */
export async function fetchPythPrices(pythAdapters: string[]): Promise<Hex[]> {
  if (pythAdapters.length === 0) return []

  const chunkSize = 10
  const chunks: string[][] = []
  for (let i = 0; i < pythAdapters.length; i += chunkSize) {
    chunks.push(pythAdapters.slice(i, i + chunkSize))
  }

  const requests = chunks.map(async (chunk) => {
    const url = `https://hermes.pyth.network/v2/updates/price/latest?ids[]=${chunk.join(
      "&ids[]=",
    )}&encoding=hex&parsed=false&ignore_invalid_price_ids=true`
    const response = await fetch(url)
    if (!response.ok) {
      console.error(response)
      throw new Error("Failed to fetch Pyth prices")
    }
    const data = (await response.json()) as PythPriceResponse
    return `0x${data.binary.data}` as Hex
  })

  const updateMessages = await Promise.all(requests)
  return updateMessages
}

/**
 * @notice Check if an adapter is a Pyth adapter
 * @param adapter The adapter to check
 * @returns True if the adapter is a Pyth adapter, false otherwise
 */
function isPythAdapter(adapter: OracleAdapter): adapter is PythAdapter {
  return adapter.name === "PythOracle"
}

/**
 * @notice Create an EVCCall for the Pyth price update
 * @param targetContract The target contract
 * @param onBehalfOfAccount The account to use for the batch item onBehalfOfAccount
 * @param fee The fee for the Pyth price update
 * @param pythData The Pyth data
 * @returns The EVCCall for the Pyth price update
 */
export function createEvcBatchItem(
  targetContract: Address,
  onBehalfOfAccount: Address,
  fee: bigint,
  pythData: Hex[],
): EVCCall {
  return {
    targetContract,
    onBehalfOfAccount: onBehalfOfAccount,
    value: fee,
    data: encodeFunctionData({
      abi: contractBook.pythOracle.abi,
      functionName: "updatePriceFeeds",
      args: [pythData],
    } as const),
  }
}

/**
 * @notice Fetch the Pyth fee for a given Pyth data
 * @param client The client
 * @param pythProxyAddress The Pyth proxy address
 * @param pythData The Pyth data
 * @returns The Pyth fee for the given Pyth data
 */
export function fetchPythFee(
  client: Client,
  pythProxyAddress: Address,
  pythData: Hex[],
) {
  return cachedQuery(
    `pyth-fee-${pythProxyAddress}-${client?.chain?.id || 1}`,
    () => client.extend(publicActions).readContract({
        address: pythProxyAddress,
        abi: contractBook.pythOracle.abi,
        functionName: "getUpdateFee",
        args: [pythData],
      }),
  )
}

/**
 * @notice Get pyth adapters
 * @param chainId the chain id
 * @returns The pyth adapters
 */
export function getPythAdapters(chainId: SupportedChainId) {
  return cachedQuery(`pyth-adapters-${chainId}`, async () => {
    const adapters = await fetchOracleAdapters(chainId)
    return adapters
  })
}
/**
 * @notice Get the Pyth price update for a given chain ID
 * @param chainId The chain ID
 * @param client The client
 * @param onBehalfOf The account to use for the batch item onBehalfOfAccount
 * @returns The Pyth price update for the given chain ID
 */
export async function getPythPriceUpdate(
  chainId: SupportedChainId,
  client: Client,
  onBehalfOf: Address = zeroAddress,
) {
  const pythProxyAddress = contractBook.pythOracle.address[chainId]

  const pythAdapters = await getPythAdapters(chainId)
  if (pythAdapters.length === 0) return []

  const pythPrices = await fetchPythPrices(pythAdapters)
  const fee = await fetchPythFee(client, pythProxyAddress, pythPrices)
  const batchItem = createEvcBatchItem(
    pythProxyAddress,
    onBehalfOf,
    fee,
    pythPrices,
  )

  return [batchItem]
}
